import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppSendHistory - "which messages have I sent from here?", asked of `/send` and turned into rows.
//
// ⚠ RULES ONLY - NO `XMLHttpRequest`, NO `window`. The Bridge suite imports this file to pin the
// field names against `SendRules.HistoryReply` (`SendHistoryContract.test.ts`), and a transport in
// here would pass under tsx and fail under tsc - the trap `AppConnect`/`AppConnectWatch` records.
// The transport is `AppDropboxSource.History`.
//
// ⛔ FOUR STATES, AND THEIR WORDS ARE THE SAME RULE AS THE LINE UNDER THE SEND BUTTON
// (`AppSignUpApi.SendLineFor`): "unconfirmed" is never "Sent" and never shown as a failure - both
// invite a second copy of a message that may already be on the recipient's phone.

/** One row as the service answers it. Must match `SendHistoryRow` / `SendRules.HistoryReply`. */
export class AppHistoryRow {
    public Id: string = "";
    public Chat: string = "";
    public At: string = "";
    /** `sent`, `unconfirmed`, `refused` or `queued`. Anything else is read as `queued`. */
    public State: string = "";
    public Why: string = "";
    public Preview: string = "";
    /** The WhatsApp JID it went to. ⚠ The answer covers EVERY chat - filter by this. */
    public ChatId: string = "";
    /** Everything the owner typed (<= 4096 chars), or "" when the service no longer has the request. */
    public Text: string = "";
    /** WhatsApp's key id once SENT, else "". A rebuilt page carries it as a bubble's `data-mid`. */
    public MessageId: string = "";
    /** A REACTION row: the message it was on, else "". ⛔ Such a row is never drawn as a bubble. */
    public ReactTo: string = "";
    /** A reaction row's emoji ("" = the owner took a reaction off). */
    public Emoji: string = "";
    /** ⭐ TRANSLATION: the words that went out when they differ from what the owner typed (`Text`), else "". */
    public SentText: string = "";
}

/** The whole answer. `Waiting`: the service is still putting its history back after a restart. */
export class AppHistoryAnswer {
    public Ok: boolean = false;
    public Waiting: boolean = false;
    public Why: string = "";
    public Rows: AppHistoryRow[] = [];
}

/** One line of the list, ready to paint. */
export class AppHistoryLine {
    public Chat: string = "";
    public When: string = "";
    /** The short state word: "Sent", "Not confirmed", "Not sent", "Waiting to send". */
    public Status: string = "";
    /** Which icon draws the state (owner 2026-11-02: an icon, not the word repeated): "sent", "warn", "wait". */
    public Icon: string = "wait";
    public Preview: string = "";
    /** The sender's sentence under a refused or unconfirmed row; "" otherwise. */
    public Note: string = "";
    /** Paint in `--owed`. ⛔ Only a REFUSAL - never an unconfirmed send. */
    public Trouble: boolean = false;
}

export type AppHistoryDone = (Answer: AppHistoryAnswer) => void;

export class AppSendHistory {
    /** ⛔ Must match `SendRules`' state constants. */
    public static readonly Sent: string = "sent";
    public static readonly Unconfirmed: string = "unconfirmed";
    public static readonly Refused: string = "refused";
    public static readonly Queued: string = "queued";
    /** ⛔ TRANSLATION: the words could not be translated - NOTHING was sent. Must equal `AppTranslate.FailedState`. */
    public static readonly TranslateFailed: string = "translate-failed";

    /** The body of "which messages have I sent?", or "" when it cannot be asked. */
    public static Body = (Phone: string, Key: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppSendHistory.ProvedBody(Phone, Key, { history: true });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSendHistory.Body");
            return "";
        }
    };

    /**
     * ⭐ ONE SHELL FOR EVERY `/send` BODY: `{number, key, ...Extra}` with both proofs trimmed, or "" when either is
     * missing. Transport-free on purpose (the Bridge suite imports this file); `AppSignUpApi.SendBody` calls it too.
     */
    public static ProvedBody = (Phone: string, Key: string, Extra: Record<string, unknown>): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Phone).trim().length === 0 || nz(Key).trim().length === 0) { return ""; }
            return JSON.stringify(Object.assign({ number: nz(Phone).trim(), key: nz(Key).trim() }, Extra));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSendHistory.ProvedBody");
            return "";
        }
    };

    /**
     * Read whatever came back.
     *
     * ⛔ THE BODY IS READ WHATEVER THE STATUS (`AppSignUpApi.Answer`'s rule): the service states its
     * refusals IN the body. ⚠ STATUS 0 OR AN UNREADABLE BODY IS `OursWhy` - our end, not the visitor's.
     */
    public static Parse = (Status: number, Text: string, OursWhy: string): AppHistoryAnswer => {
        if (getCoreUtils().KErrorMode === true) { return AppSendHistory.No(OursWhy); }
        try {
            if (Status === 0) { return AppSendHistory.No(OursWhy); }
            const Raw: unknown = JSON.parse(nz(Text));
            if (Raw === null || typeof Raw !== "object") { return AppSendHistory.No(OursWhy); }
            return AppSendHistory.Fields(Raw as Record<string, unknown>);
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an answer this build cannot parse is EXPECTED on a public
            // wire (a proxy error page, a truncated reply) - fate 1, shown as our end.
            return AppSendHistory.No(OursWhy);
        }
    };

    /**
     * DOCUMENTED EXCEPTION: reads fields already parsed; called only from the guarded `Parse`.
     * ERROR-SUPPRESSED-JUSTIFIED: pure reader. ⛔ Booleans are read as strictly `true`.
     */
    private static Fields = (Raw: Record<string, unknown>): AppHistoryAnswer => {
        const Out: AppHistoryAnswer = new AppHistoryAnswer();
        Out.Ok = Raw.ok === true;
        Out.Why = nz(Raw.why as string);
        Out.Waiting = Raw.waiting === true;
        if (Array.isArray(Raw.history) === false) { return Out; }
        for (const One of Raw.history as unknown[]) {
            if (One === null || typeof One !== "object") { continue; }
            Out.Rows.push(AppSendHistory.RowOf(One as Record<string, unknown>));
        }
        return Out;
    };

    /** DOCUMENTED EXCEPTION: reads one parsed object. ERROR-SUPPRESSED-JUSTIFIED: pure reader. */
    private static RowOf = (Raw: Record<string, unknown>): AppHistoryRow => {
        const Row: AppHistoryRow = new AppHistoryRow();
        Row.Id = AppSendHistory.Str(Raw.id);
        Row.Chat = AppSendHistory.Str(Raw.chat);
        Row.At = AppSendHistory.Str(Raw.at);
        Row.State = AppSendHistory.Str(Raw.state);
        Row.Why = AppSendHistory.Str(Raw.why);
        Row.Preview = AppSendHistory.Str(Raw.preview);
        Row.ChatId = AppSendHistory.Str(Raw.chatId);
        Row.Text = AppSendHistory.Str(Raw.text);
        Row.MessageId = AppSendHistory.Str(Raw.messageId);
        Row.SentText = AppSendHistory.Str(Raw.sentText);
        // ⚠ `SendRules.HistoryReply` writes `reaction` only on a reaction row - pinned by `ReactContract.test.ts`.
        if (Raw.reaction !== null && typeof Raw.reaction === "object") {
            Row.ReactTo = AppSendHistory.Str((Raw.reaction as Record<string, unknown>).messageId);
            Row.Emoji = AppSendHistory.Str((Raw.reaction as Record<string, unknown>).emoji);
        }
        return Row;
    };

    /** DOCUMENTED EXCEPTION: a string, or "". ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw. */
    private static Str = (Value: unknown): string => {
        if (typeof Value !== "string") { return ""; }
        return Value;
    };

    /**
     * What one row says on the page.
     *
     * ⛔ UNCONFIRMED IS INK, NOT ORANGE, AND ITS NOTE SAYS TO CHECK THE PHONE. An unknown state reads
     * as "Waiting to send", never as "Sent" - claiming a delivery nobody confirmed is the one wrong
     * answer this list must not give.
     */
    public static LineOf = (Row: AppHistoryRow, NowMs: number): AppHistoryLine => {
        if (getCoreUtils().KErrorMode === true) { return new AppHistoryLine(); }
        try {
            const Out: AppHistoryLine = new AppHistoryLine();
            Out.Chat = nz(Row.Chat).trim();
            if (Out.Chat.length === 0) { Out.Chat = "A chat"; }
            Out.When = AppSendHistory.WhenOf(Row.At, NowMs);
            Out.Preview = nz(Row.Preview);
            Out.Status = "Waiting to send";
            if (Row.State === AppSendHistory.Sent) { Out.Status = "Sent"; Out.Icon = "sent"; }
            if (Row.State === AppSendHistory.Unconfirmed) {
                Out.Status = "Not confirmed";
                Out.Icon = "warn";
                Out.Note = AppSendHistory.UnconfirmedNote(Row.Why);
            }
            if (Row.State === AppSendHistory.Refused) {
                Out.Status = "Not sent";
                Out.Icon = "warn";
                Out.Note = nz(Row.Why);
                Out.Trouble = true;
            }
            if (Row.State === AppSendHistory.TranslateFailed) {
                Out.Status = "Not sent";
                Out.Icon = "warn";
                Out.Note = "Couldn't translate it, so nothing was sent. Open the chat to retry or send it untranslated.";
                Out.Trouble = true;
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSendHistory.LineOf");
            return new AppHistoryLine();
        }
    };

    /** Every row, in the order the service gave (newest first). */
    public static LinesOf = (Answer: AppHistoryAnswer, NowMs: number): AppHistoryLine[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppHistoryLine[] = [];
            for (const Row of Answer.Rows) { Out.push(AppSendHistory.LineOf(Row, NowMs)); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSendHistory.LinesOf");
            return [];
        }
    };

    /**
     * The one sentence above the list, or "" when the list speaks for itself.
     *
     * ⚠ `Waiting` IS NOT "YOU HAVE SENT NOTHING". Right after a restart the service is still
     * putting the history back, and an empty list then would read as "your messages are gone".
     */
    public static HeadOf = (Answer: AppHistoryAnswer): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Answer.Ok !== true) { return nz(Answer.Why); }
            if (Answer.Waiting === true) {
                return "Your sent messages are still being loaded. Try again in a minute.";
            }
            if (Answer.Rows.length === 0) { return "Nothing has been sent from WACommander yet."; }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSendHistory.HeadOf");
            return "";
        }
    };

    /**
     * "14:05" today, "23 Sep 14:05" otherwise; "" for a time that cannot be read.
     * ⚠ In the browser's own locale and time zone - these are the owner's own messages.
     */
    public static WhenOf = (Iso: string, NowMs: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const At: number = Date.parse(nz(Iso));
            if (isNaN(At) === true) { return ""; }
            const Then: Date = new Date(At);
            const Clock: string = Then.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
            if (Then.toDateString() === new Date(NowMs).toDateString()) { return Clock; }
            return Then.toLocaleDateString(undefined, { day: "numeric", month: "short" }) + " " + Clock;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSendHistory.WhenOf");
            return "";
        }
    };

    /** ⛔ Always tells the owner to check the phone - the sender's sentence, or this one. */
    public static UnconfirmedNote = (Why: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Why).trim().length > 0) { return nz(Why).trim(); }
            return "WhatsApp did not confirm this message, so it may or may not have been delivered."
                + " Check the chat on your phone before sending it again.";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSendHistory.UnconfirmedNote");
            return "";
        }
    };

    /** DOCUMENTED EXCEPTION: builds a refusal from a sentence in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static No = (Why: string): AppHistoryAnswer => {
        const Out: AppHistoryAnswer = new AppHistoryAnswer();
        Out.Ok = false;
        Out.Why = nz(Why);
        return Out;
    };
}
